// src/pages/settings/TeamSettings.jsx
// Team management page showing members, seats, and invite functionality

import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Button,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  Avatar,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import PersonAddRoundedIcon from "@mui/icons-material/PersonAddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import AdminPanelSettingsRoundedIcon from "@mui/icons-material/AdminPanelSettingsRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import BusinessRoundedIcon from "@mui/icons-material/BusinessRounded";

import { useSubscription } from "../../context/SubscriptionContext";
import { teamService } from "../../api/teamService";
import { SeatsProgressBar } from "../../components/SeatsBadge";
import InviteUserModal from "../../components/InviteUserModal";
import InvitesTable from "../../components/InvitesTable";
import UpgradeBanner from "../../components/UpgradeBanner";

export default function TeamSettings() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { seats, loading: seatsLoading, refreshSeats } = useSubscription();

  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  // Get current user from localStorage to check permissions
  const currentUser = JSON.parse(
    localStorage.getItem("agentBlayna_user") || "{}"
  );
  const isAdmin = currentUser?.role === "admin";

  useEffect(() => {
    fetchMembers();
  }, []);

  const fetchMembers = async () => {
    try {
      setLoading(true);
      setError("");
      const membersData = await teamService.getMembers().catch(() => []);
      setMembers(Array.isArray(membersData) ? membersData : []);
    } catch (err) {
      console.error("Failed to fetch team members:", err);
      setError("Failed to load team information");
    } finally {
      setLoading(false);
    }
  };

  const handleInviteSuccess = () => {
    setInviteModalOpen(false);
    refreshSeats();
  };

  const handleRemoveMember = async (memberId) => {
    if (
      !window.confirm("Are you sure you want to remove this team member?")
    ) {
      return;
    }

    try {
      await teamService.removeMember(memberId);
      setMembers((prev) => prev.filter((m) => m.id !== memberId));
      refreshSeats();
    } catch (err) {
      setError(err.message);
    }
  };

  const getRoleIcon = (role) => {
    return role === "admin" ? (
      <AdminPanelSettingsRoundedIcon
        sx={{ fontSize: 16, color: "primary.main" }}
      />
    ) : (
      <PersonRoundedIcon sx={{ fontSize: 16, color: "text.secondary" }} />
    );
  };

  const getRoleLabel = (role) => {
    return role === "admin" ? "Admin" : "Agent";
  };

  if (seatsLoading || loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 900, mx: "auto", p: 3 }}>
      <Typography variant="h4" fontWeight={700} gutterBottom>
        Team Settings
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Manage your team members and invitations.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {/* Upgrade Banner when seats are full */}
      {seats && !seats.can_add_members && <UpgradeBanner />}

      {/* Organization Info */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <BusinessRoundedIcon color="primary" />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {seats?.organization_name || "My Organization"}
            </Typography>
            <Chip
              size="small"
              label={
                seats?.subscription_plan
                  ? `${seats.subscription_plan} Plan`
                  : "Trial"
              }
              color={seats?.subscription_plan ? "primary" : "default"}
              sx={{ mt: 0.5, textTransform: "capitalize" }}
            />
          </Box>
        </Stack>

        {/* Seats Progress */}
        <SeatsProgressBar />

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          {seats?.seats_available > 0
            ? `You can add ${seats.seats_available} more team member${
                seats.seats_available > 1 ? "s" : ""
              }.`
            : "You've reached the seat limit for your plan."}
        </Typography>
      </Paper>

      {/* Team Members */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
        >
          <Typography variant="h6" fontWeight={600}>
            Team Members ({members.length})
          </Typography>

          {isAdmin && seats?.can_add_members && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PersonAddRoundedIcon />}
              onClick={() => setInviteModalOpen(true)}
            >
              Invite Member
            </Button>
          )}
        </Stack>

        {members.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No team members yet. Invite someone to get started!
          </Typography>
        ) : (
          <List disablePadding>
            {members.map((member, index) => (
              <React.Fragment key={member.id}>
                {index > 0 && <Divider />}
                <ListItem sx={{ px: 0, py: 1.5 }}>
                  <ListItemAvatar>
                    <Avatar
                      sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1) }}
                    >
                      {member.name?.charAt(0)?.toUpperCase() || "U"}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body1" fontWeight={500}>
                          {member.name || member.email}
                        </Typography>
                        {member.id === currentUser?.id && (
                          <Chip size="small" label="You" variant="outlined" />
                        )}
                      </Stack>
                    }
                    secondary={
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={0.5}
                        sx={{ mt: 0.5 }}
                      >
                        {getRoleIcon(member.role)}
                        <Typography variant="caption" color="text.secondary">
                          {getRoleLabel(member.role)}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mx: 1 }}
                        >
                          •
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {member.email}
                        </Typography>
                      </Stack>
                    }
                  />
                  {isAdmin && member.id !== currentUser?.id && (
                    <ListItemSecondaryAction>
                      <Tooltip title="Remove member">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={() => handleRemoveMember(member.id)}
                          sx={{
                            color: "text.secondary",
                            "&:hover": { color: "error.main" },
                          }}
                        >
                          <DeleteOutlineRoundedIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Invitation Codes (Admin only) */}
      {isAdmin && (
        <Paper sx={{ p: 3, borderRadius: 2 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 2 }}
          >
            <Typography variant="h6" fontWeight={600}>
              Invitation Codes
            </Typography>
            {seats?.can_add_members && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PersonAddRoundedIcon />}
                onClick={() => setInviteModalOpen(true)}
              >
                Create New Code
              </Button>
            )}
          </Stack>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Share these codes with new team members to let them join your
            organization.
          </Typography>

          <InvitesTable />
        </Paper>
      )}

      {/* Non-admin notice */}
      {!isAdmin && (
        <Alert severity="info" sx={{ mt: 3 }}>
          Only admins can invite new members or manage the team. Contact your
          organization admin for changes.
        </Alert>
      )}

      {/* Invite Modal */}
      <InviteUserModal
        open={inviteModalOpen}
        onClose={() => setInviteModalOpen(false)}
        onSuccess={handleInviteSuccess}
      />
    </Box>
  );
}
